// Batch questions (D64): an agent may ask several questions at once (`ask_user --file`). Each item
// is its own pending question with `batch_id`, `batch_item` and `batch_size`; the run resumes once
// the last is answered. `GET /api/runs/{id}/questions` lists only the pending ones, so a batch's
// progress is its size minus the items still pending.
import type { Question } from "../api/client";
import { messages } from "../i18n";

export type PendingGroup =
  | { kind: "single"; question: Question }
  | { kind: "batch"; batchId: string; size: number; answered: number; pending: Question[] };

interface BatchFields {
  batch_id?: string | null;
  batch_item?: string | null;
  batch_size?: number | null;
}

// Read loosely: the generated schema gains these with S56.
const batchOf = (q: Question): BatchFields => q as BatchFields;

/** The item's id from the agent's questions file, for a batch question. */
export const batchItem = (q: Question): string | undefined => batchOf(q).batch_item ?? undefined;

/** Pending questions in the order asked, a batch's items together where its first item was. */
export function groupPending(questions: Question[]): PendingGroup[] {
  const groups: PendingGroup[] = [];
  const batches = new Map<string, PendingGroup & { kind: "batch" }>();
  for (const q of [...questions].sort((a, b) => a.created_at - b.created_at)) {
    const { batch_id: id, batch_size: size } = batchOf(q);
    if (!id) {
      groups.push({ kind: "single", question: q });
      continue;
    }
    let batch = batches.get(id);
    if (!batch) {
      batch = { kind: "batch", batchId: id, size: size ?? 0, answered: 0, pending: [] };
      batches.set(id, batch);
      groups.push(batch);
    }
    batch.pending.push(q);
    batch.size = Math.max(batch.size, size ?? 0, batch.pending.length);
  }
  for (const b of batches.values()) b.answered = b.size - b.pending.length;
  return groups;
}

/** "2 of 4 answered". */
export const batchProgress = (b: { size: number; answered: number }): string => messages().tasks.task.batchProgress(b.answered, b.size);

/** One line per pending question or batch: metadata only, for the timeline. */
export function pendingSummary(questions: Question[]): string[] {
  const t = messages().tasks.task;
  return groupPending(questions).map((g) => (g.kind === "single" ? t.questionWaiting : t.batchSummary(g.size, batchProgress(g))));
}
